import { useCallback, useEffect, useMemo, useState } from 'react';
import reminderService from '../services/reminderService';
import { debounce } from '../utils/debounce';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import { countUnseen, readSeen, writeSeen, SEEN_STORAGE_KEY } from '../utils/notificationSeen';

export const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

// Calendar-event notifications surface automatically (upcoming confirmed events)
// and have no server-side "cleared" flag, so the user's dismissal of one via the
// "X" is remembered locally — it stays hidden until the event passes and drops off
// the feed on its own.
const DISMISSED_EVENTS_KEY = 'omnis:dismissed-bell-events';
const readDismissedEvents = () => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_EVENTS_KEY) || '[]')); }
    catch (_) { return new Set(); }
};
const addDismissedEvent = (id) => {
    try {
        const set = readDismissedEvents();
        set.add(id);
        localStorage.setItem(DISMISSED_EVENTS_KEY, JSON.stringify([...set]));
    } catch (_) { /* ignore */ }
};

/**
 * The Bell feed (GET /api/reminders) — fetch, poll, live refetch and dismissal.
 *
 * EXTRACTED from RemindersDropdown (#437) so the feed has ONE implementation for
 * its two surfaces: the Reminders & Notifications panel's Notifications column
 * (whose data lives in WorkspaceShell, so the sidebar's red dot and the column
 * read the same list) and the day-channel focus view's bell dropdown. Behaviour is
 * the dropdown's, unchanged — "the underlying behavior of notifications remains
 * the same; only the visual container and location are updated".
 *
 * `pollMs` is the background refresh; `fastPollMs` (optional) a tighter one used
 * while the Notifications column is on screen, so an arrival shows without the
 * user reopening anything.
 */
export function useNotificationFeed({ pollMs = REFRESH_INTERVAL_MS, fastPollMs = null } = {}) {
    const { t } = useLanguage();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Bumped when the "seen" set changes (here or in another tab), so the
    // derived unseen count recomputes.
    const [seenVersion, setSeenVersion] = useState(0);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await reminderService.listReminders();
            const dismissed = readDismissedEvents();
            setItems((res.items || []).filter(
                (it) => !(it.type === 'calendar_event' && dismissed.has(it.id))
            ));
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const interval = fastPollMs || pollMs;
    useEffect(() => {
        refresh();
        const id = setInterval(refresh, interval);
        return () => clearInterval(id);
    }, [refresh, interval]);

    useEffect(() => {
        // One omnis:* event fires per row; trailing-debounce so a burst → one fetch.
        const handle = debounce(() => refresh(), 300);
        // PAI editing (update_reminder) / deleting (delete_reminders) a reminder,
        // the JIT firing (reminder_due → omnis:reminder-updated), and unassigned
        // expense saves (a proactive notification) all refetch the feed live.
        const events = ['omnis:reminder-created', 'omnis:reminder-updated',
            'omnis:reminder-deleted', 'omnis:expense-created'];
        events.forEach((e) => window.addEventListener(e, handle));
        return () => {
            handle.cancel();
            events.forEach((e) => window.removeEventListener(e, handle));
        };
    }, [refresh]);

    useEffect(() => {
        const onStorage = (e) => { if (e.key === SEEN_STORAGE_KEY) setSeenVersion((v) => v + 1); };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const clear = useCallback(async (item) => {
        if (item.type === 'calendar_event') {
            // Upcoming-event notifications have no server-side cleared flag — remember
            // the dismissal locally so it stays gone (the event itself is untouched).
            addDismissedEvent(item.id);
            setItems((cur) => cur.filter((x) => x.id !== item.id));
            return;
        }
        if (item.type !== 'reminder') return;
        setItems((cur) => cur.filter((x) => x.id !== item.id));
        try {
            await reminderService.clearReminder(item.id);
            // Bell notification reminder (#257): clearing DISMISSES THE ALERT ONLY —
            // a plain reminder lives on in the Reminders column until it is
            // explicitly deleted there. Broadcast so that column re-renders.
            window.dispatchEvent(new CustomEvent('omnis:reminder-updated', { detail: { id: item.id } }));
        } catch (err) {
            setError(userErrorMessage(err, t));
            refresh();  // put the row back from the server's truth
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh]);

    const unseenCount = useMemo(
        () => countUnseen(items, readSeen()),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, seenVersion],
    );

    const markAllSeen = useCallback(() => {
        writeSeen(items);
        setSeenVersion((v) => v + 1);
    }, [items]);

    return { items, loading, error, refresh, clear, unseenCount, markAllSeen };
}

export default useNotificationFeed;
